/**
 * School uniforms, drawn with the modular figure's clothes: each school dresses its men and its
 * women differently (a Taoist robe and a priestess's robe, a monk's kasaya and a lay sister's
 * fighting clothes...) in the school's colours. A figure in a school's look wears its uniform in
 * place of the clothes its classic look describes; hair, face, headwear it chose and the weapon are
 * its own.
 */
import { type BottomLook, bottomLayer, robeLayer, type RobeLook, topLayer, type TopLook } from './clothes';
import type { Layer } from './figure';
import { headwearLayer } from './figure';
import {
  beltLayer,
  type BeltLook,
  capeLayer,
  type CapeLook,
  pauldronLayer,
  type PauldronLook,
  shoesLayer,
  type ShoeLook,
} from './gear';
import { type Regalia, regaliaLayer } from './regalia';

export interface Uniform {
  top: TopLook;
  bottom: BottomLook;
  robe?: RobeLook;
  shoes: ShoeLook;
  belt?: BeltLook;
  cape?: CapeLook;
  pauldrons?: PauldronLook;
  /** Headwear that is part of the uniform (an official's cap), worn instead of the figure's own. */
  headwear?: readonly string[];
  /** The school's ornaments: emblems, beads, pendants, sash tails. */
  regalia?: readonly Regalia[];
  /** The colour of the mantles and capes of rank (uniformTier); the trim if left out. */
  mantle?: string;
  /**
   * Costume read pass on the robe, drawn natively (outfit2x/garments.ts `ReadPass`): the sleeve lining colour shown
   * where a sleeve lies over the robe, and the long skirt folds (0 left edge … 1 right edge, as seen).
   */
  read?: { rim: string; folds: readonly number[]; ribbon?: string };
}

export interface SchoolUniform {
  label: string;
  male: Uniform;
  female: Uniform;
}

export const UNIFORMS: Readonly<Record<string, SchoolUniform>> = {
  'mist-peak': {
    label: 'ยอดหมอก',
    // Ivory Taoist robes edged in ink — yin and yang — in the lifelike finish: plain silk that
    // reads by its light, folds and sheen rather than a woven pattern, cloud scrolls only round the
    // hem, a taiji on the back and a white jade at the sash; long ink sash ends streaming. The
    // priestesses add an ink cloud collar over the shoulders.
    male: {
      top: { id: 'cross', colour: '#2a2d38', trim: '#e9e6de', inner: '#e9e6de', accent: '#8298b6' },
      bottom: { id: 'wrapped', colour: '#2a2d38', wrap: '#e9e6de' },
      robe: {
        id: 'daopao',
        colour: '#e9e6de',
        trim: '#2a2d38',
        inner: '#2a2d38',
        sash: '#2a2d38',
        accent: '#8298b6',
        hem: 'clouds',
        // BODY dressed-r2 D2: a deeper sash and side-panel seams, so the straight robe reads as a man's cut.
        panel: true,
        sashRows: 3,
      },
      shoes: { id: 'cloth', colour: '#1a1a20' },
      regalia: [
        { id: 'taiji', c: '#f2f0ea', s: '#1c1e28', a: '#8298b6' },
        { id: 'jade', c: '#dcefe2', s: '#8fb8a0', a: '#2a2d38' },
        // The wind ribbon (costume §4.2): mist blue, so the sash breaks the white column in every view.
        { id: 'sashTails', c: '#8298b6', s: '#5d7393', a: '#2a2d38' },
      ],
      mantle: '#2a2d38',
      // Costume read fix (10-11): two long folds, the sleeve's mist-blue lining where it lies over the robe.
      read: { rim: '#6c84a6', folds: [0.3, 0.7], ribbon: '#8298b6' },
    },
    female: {
      top: { id: 'cross', colour: '#2a2d38', trim: '#e9e6de', inner: '#e9e6de', accent: '#8298b6' },
      bottom: { id: 'skirt', colour: '#2a2d38', wrap: '#e9e6de' },
      robe: {
        id: 'fairy',
        colour: '#e9e6de',
        trim: '#2a2d38',
        inner: '#2a2d38',
        sash: '#2a2d38',
        accent: '#8298b6',
        hem: 'clouds',
        // BODY dressed-r3 E1: a slimmer column (hem flare 1, no train, a narrower opening), so her robe stays inside his
        // shoulders and her waist shows.
        hemFlare: 1,
        noTrain: true,
        slitScale: 0.5,
        sashRows: 2,
      },
      shoes: { id: 'slippers', colour: '#1a1a20' },
      cape: { id: 'shawl', colour: '#2a2d38', clasp: '#2a2d38' },
      regalia: [
        { id: 'cloudCollar', c: '#2a2d38', s: '#16171e', a: '#e9e6de' },
        { id: 'taiji', c: '#f2f0ea', s: '#1c1e28', a: '#8298b6' },
        { id: 'jade', c: '#dcefe2', s: '#8fb8a0', a: '#2a2d38' },
        { id: 'sashTails', c: '#8298b6', s: '#5d7393', a: '#2a2d38' },
      ],
      mantle: '#2a2d38',
      read: { rim: '#6c84a6', folds: [0.28, 0.66], ribbon: '#8298b6' },
    },
  },
  'bell-stone': {
    label: 'หินระฆัง',
    // Saffron under a red kasaya edged and piped in gold, a row of gold lotus round the hem, great
    // wooden prayer beads and gold armlets; lay sisters in saffron fighting clothes with the beads.
    male: {
      top: { id: 'bare', colour: '#c9782a', trim: '#6a3218', inner: '#e8d6aa', accent: '#d8aa48' },
      bottom: { id: 'monk', colour: '#c9782a', wrap: '#e8d6aa' },
      robe: {
        id: 'kasaya',
        colour: '#8a2e22',
        trim: '#d8aa48',
        inner: '#c9782a',
        sash: '#6a3218',
        accent: '#4e1a12',
        hem: 'lotus',
        brocade: 'lattice',
      },
      shoes: { id: 'wrapped', colour: '#4a3424' },
      regalia: [
        { id: 'mala', c: '#6a3a1c', s: '#3a1c0c', a: '#a8242a' },
        { id: 'armlets', c: '#d8aa48', s: '#96701e', a: '#a8242a' },
      ],
      mantle: '#6e2418',
    },
    female: {
      top: {
        id: 'swordmaid',
        colour: '#c9782a',
        trim: '#6a3218',
        inner: '#e8d6aa',
        accent: '#d8aa48',
        brocade: 'fret',
      },
      bottom: { id: 'slim', colour: '#6a3218', wrap: '#e8d6aa' },
      shoes: { id: 'cloth', colour: '#4a3424' },
      belt: { id: 'bow', colour: '#6a3218', buckle: '#d8aa48' },
      cape: { id: 'mantle', colour: '#8a2e22', clasp: '#d8aa48' },
      regalia: [
        { id: 'mala', c: '#6a3a1c', s: '#3a1c0c', a: '#a8242a' },
        { id: 'tassels', c: '#a8242a', s: '#7a1018', a: '#d8aa48' },
      ],
      mantle: '#6e2418',
    },
  },
  'lantern-road': {
    label: 'ทางโคมเหล็ก',
    // The crimson lamp-spark robe: a gold flying fish coiling over the breast and back, sea waves
    // breaking on rocks round the hem, gold piping, the gilt waist token and the black official's
    // cap; the women in the same crimson robe slit for riding, a black cape over it.
    male: {
      top: { id: 'mandarin', colour: '#1c1c24', trim: '#d4a84a', inner: '#1c1c24', accent: '#2a5e52' },
      bottom: { id: 'trousers', colour: '#1c1c24', wrap: '#d4a84a' },
      robe: {
        id: 'feiyu',
        colour: '#7e1a20',
        trim: '#d4a84a',
        inner: '#1c1c24',
        sash: '#1c1c24',
        accent: '#2a6a5e',
        hem: 'waves',
        brocade: 'scales',
      },
      shoes: { id: 'tallboots', colour: '#16161c' },
      belt: { id: 'jade', colour: '#1c1c24', buckle: '#d4a84a' },
      headwear: ['futou'],
      regalia: [
        { id: 'flyingFish', c: '#dcb052', s: '#a87a1a', a: '#2e8678' },
        { id: 'token', c: '#d4a84a', s: '#8a6420', a: '#a8242a' },
      ],
      mantle: '#1c1c24',
    },
    female: {
      top: { id: 'longstand', colour: '#7e1a20', trim: '#d4a84a', inner: '#1c1c24', accent: '#2a6a5e' },
      bottom: { id: 'slim', colour: '#1c1c24', wrap: '#d4a84a' },
      robe: {
        id: 'swordlady',
        colour: '#7e1a20',
        trim: '#d4a84a',
        inner: '#1c1c24',
        sash: '#1c1c24',
        accent: '#2a6a5e',
        hem: 'waves',
        brocade: 'scales',
      },
      shoes: { id: 'highboots', colour: '#16161c' },
      belt: { id: 'wide', colour: '#1c1c24', buckle: '#d4a84a' },
      cape: { id: 'mantle', colour: '#1c1c24', clasp: '#d4a84a' },
      regalia: [
        { id: 'flyingFish', c: '#dcb052', s: '#a87a1a', a: '#2e8678' },
        { id: 'token', c: '#d4a84a', s: '#8a6420', a: '#a8242a' },
      ],
      mantle: '#1c1c24',
    },
  },
  'dim-moon': {
    label: 'จันทร์มัว',
    // Deep purple and black edged in pink and piped in gold, peonies round the hem, silk tassels at
    // the sash; the women add a pink cloud collar and a rose-quartz pendant to the dancer's robe.
    male: {
      top: { id: 'open', colour: '#35183f', trim: '#c8608e', inner: '#e8d4de', accent: '#d6b070' },
      bottom: { id: 'wide', colour: '#1a0e22', wrap: '#c8608e' },
      robe: {
        id: 'coat',
        colour: '#1a0e22',
        trim: '#c8608e',
        inner: '#35183f',
        sash: '#c8608e',
        accent: '#d6b070',
        hem: 'peony',
        brocade: 'blossoms',
      },
      shoes: { id: 'curled', colour: '#1a1020' },
      cape: { id: 'scarf', colour: '#c8608e', clasp: '#e8d4de' },
      regalia: [
        { id: 'tassels', c: '#c8608e', s: '#8e3862', a: '#d6b070' },
        { id: 'armlets', c: '#d6b070', s: '#8e6c2e', a: '#c8608e' },
      ],
      mantle: '#2a1236',
    },
    female: {
      top: { id: 'cross', colour: '#e8d4de', trim: '#c8608e', inner: '#e8d4de', accent: '#d6b070' },
      bottom: { id: 'skirt', colour: '#1a0e22', wrap: '#c8608e' },
      robe: {
        id: 'dancer',
        colour: '#35183f',
        trim: '#c8608e',
        inner: '#e8d4de',
        sash: '#c8608e',
        accent: '#d6b070',
        hem: 'peony',
        brocade: 'blossoms',
      },
      shoes: { id: 'lotus', colour: '#1a1020' },
      cape: { id: 'shawl', colour: '#c8608e', clasp: '#e8d4de' },
      regalia: [
        { id: 'cloudCollar', c: '#4e2660', s: '#35183f', a: '#c8608e' },
        { id: 'jade', c: '#eac2d2', s: '#b07e96', a: '#c8608e' },
        { id: 'tassels', c: '#c8608e', s: '#8e3862', a: '#d6b070' },
      ],
      mantle: '#2a1236',
    },
  },
  'pine-ink': {
    label: 'หอหมึกสน',
    // Pale scholars' robes trimmed in sky blue and piped in ink, plain silk with bamboo round the
    // hem, a green jade pendant on a blue cord; the women in high-waisted ruqun, a blue cloud
    // collar and shawl.
    male: {
      top: { id: 'cross', colour: '#35628c', trim: '#e4e9ec', inner: '#e4e9ec', accent: '#28303c' },
      bottom: { id: 'trousers', colour: '#28303c', wrap: '#e4e9ec' },
      robe: {
        id: 'scholar',
        colour: '#e4e9ec',
        trim: '#35628c',
        inner: '#35628c',
        sash: '#35628c',
        accent: '#548c62',
        hem: 'bamboo',
      },
      shoes: { id: 'cloth', colour: '#28303c' },
      regalia: [
        { id: 'jade', c: '#78bf98', s: '#387c54', a: '#35628c' },
        { id: 'sashTails', c: '#35628c', s: '#25415e', a: '#e4e9ec' },
      ],
      mantle: '#35628c',
    },
    female: {
      top: { id: 'cross', colour: '#e4e9ec', trim: '#35628c', inner: '#e4e9ec', accent: '#93aec6' },
      bottom: { id: 'pleated', colour: '#93aec6', wrap: '#e4e9ec' },
      robe: {
        id: 'ruqun',
        colour: '#e4e9ec',
        trim: '#35628c',
        inner: '#93aec6',
        sash: '#35628c',
        accent: '#548c62',
        hem: 'bamboo',
      },
      shoes: { id: 'slippers', colour: '#28303c' },
      cape: { id: 'shawl', colour: '#93aec6', clasp: '#35628c' },
      regalia: [
        { id: 'cloudCollar', c: '#35628c', s: '#25415e', a: '#e4e9ec' },
        { id: 'jade', c: '#78bf98', s: '#387c54', a: '#35628c' },
      ],
      mantle: '#35628c',
    },
  },
  'cinnabar': {
    label: 'ดาบชาด',
    // Black war coats over crimson, flames licking up the hem, armour at the shins and shoulder,
    // iron armlets, a tattered red cape and long blood-red sash ends streaming behind.
    male: {
      top: { id: 'bracer', colour: '#6e0e14', trim: '#1a0a0c', inner: '#1a1416', accent: '#a81e26' },
      bottom: { id: 'greaves', colour: '#1a1416', wrap: '#3a0a0e' },
      robe: {
        id: 'battle',
        colour: '#1a0e10',
        trim: '#a81e26',
        inner: '#6e0e14',
        sash: '#2a0e10',
        accent: '#e86a2a',
        hem: 'flames',
        brocade: 'lattice',
      },
      shoes: { id: 'armoured', colour: '#140e10' },
      pauldrons: { id: 'single', plate: '#2a0a0e', edge: '#a81e26' },
      cape: { id: 'tattered', colour: '#6e0e14', clasp: '#a81e26' },
      regalia: [
        { id: 'sashTails', c: '#94141c', s: '#6a0a10', a: '#1a0a0c' },
        { id: 'armlets', c: '#4a3a3e', s: '#2a2024', a: '#a81e26' },
      ],
      mantle: '#5a0a10',
    },
    female: {
      top: { id: 'swordmaid', colour: '#6e0e14', trim: '#1a0a0c', inner: '#1a1416', accent: '#a81e26' },
      bottom: { id: 'slim', colour: '#1a1416', wrap: '#6e0e14' },
      robe: {
        id: 'swordlady',
        colour: '#1a0e10',
        trim: '#a81e26',
        inner: '#6e0e14',
        sash: '#2a0e10',
        accent: '#e86a2a',
        hem: 'flames',
      },
      shoes: { id: 'highboots', colour: '#140e10' },
      pauldrons: { id: 'winged', plate: '#2a0a0e', edge: '#a81e26' },
      cape: { id: 'feather', colour: '#6e0e14', clasp: '#a81e26' },
      regalia: [
        { id: 'sashTails', c: '#94141c', s: '#6a0a10', a: '#1a0a0c' },
        { id: 'tassels', c: '#a81e26', s: '#6a0a10', a: '#2a0a0e' },
      ],
      mantle: '#5a0a10',
    },
  },
};

/** The metal of each badge rank, as its badge (data/badges.ts): iron, bronze, silver, gold. */
const METALS: readonly { c: string; s: string }[] = [
  { c: '#9aa0a8', s: '#585e66' },
  { c: '#9aa0a8', s: '#585e66' },
  { c: '#c88a4a', s: '#7a4a22' },
  { c: '#e4e8ee', s: '#8a92a0' },
  { c: '#e8c27e', s: '#a8823e' },
];

/**
 * A school uniform at a badge rank (ป้ายสำนัก): the higher the rank, the grander, while every
 * school keeps its own cut and colours, and its metal follows the badge — iron, bronze, silver,
 * gold.
 * 0 (disciple) plain cloth: no embroidered hem, only the school's emblem.
 * 1 (inner disciple) the full uniform — embroidered hem and the school's ornaments — an iron
 *   token and a belt.
 * 2 (senior disciple) a shoulder mantle, quilted or leather shoulders, armlets, bronze fittings.
 * 3 (elder) silver piping, a long silver-edged cape, round guards, tassels and a silver hairpin.
 * 4 (guardian) gold trim on every edge, cloud brocade, a cape to the ground banded twice in gold,
 *   dragon or phoenix-wing guards and a crown.
 */
export function uniformTier(u: Uniform | undefined, tier: number, female: boolean): Uniform | undefined {
  if (!u) return u;
  if (tier <= 0)
    return {
      ...u,
      top: { ...u.top, brocade: undefined },
      ...(u.robe ? { robe: { ...u.robe, hem: undefined, brocade: undefined } } : {}),
      regalia: (u.regalia ?? []).slice(0, 1),
    };
  const m = METALS[Math.min(tier, METALS.length - 1)]!;
  const trim = u.robe?.trim ?? u.top.trim;
  const mantle = u.mantle ?? trim;
  const out: Uniform = { ...u, regalia: [...(u.regalia ?? [])] };
  const adorn = (r: Regalia) => {
    out.regalia = [...out.regalia!.filter((x) => x.id !== r.id), r];
  };
  adorn({ id: 'token', c: m.c, s: m.s, a: trim });
  out.belt = { ...(u.belt ?? { id: female ? 'obi' : 'leather', colour: trim }), buckle: m.c };
  if (tier >= 2) {
    out.cape = { id: 'mantle', colour: mantle, clasp: m.c };
    out.pauldrons = { id: female ? 'quilted' : 'leather', plate: mantle, edge: m.c };
    adorn({ id: 'armlets', c: m.c, s: m.s, a: trim });
  }
  if (tier >= 3) {
    out.cape = { id: 'long', colour: mantle, clasp: m.c, trim: m.c };
    out.pauldrons = { id: 'round', plate: mantle, edge: m.c };
    adorn({ id: 'tassels', c: m.c, s: m.s, a: trim });
    // Piping in the rank's metal along every edge.
    out.top = { ...out.top, accent: m.c };
    if (out.robe) out.robe = { ...out.robe, accent: m.c };
    if (!u.headwear?.length) out.headwear = ['pin'];
  }
  if (tier >= 4) {
    out.cape = { id: 'floor', colour: mantle, clasp: m.c, trim: m.c, double: true };
    out.pauldrons = { id: female ? 'winged' : 'dragon', plate: mantle, edge: m.c };
    out.top = { ...out.top, trim: m.c };
    if (out.robe) out.robe = { ...out.robe, trim: m.c, brocade: out.robe.brocade ?? 'clouds' };
    if (!u.headwear?.length) out.headwear = [female ? 'phoenix' : 'guan'];
  }
  return out;
}

/** The clothes of a uniform, as layers. */
export function uniformLayers(u: Uniform): Layer[] {
  const out: Layer[] = [bottomLayer(u.bottom), shoesLayer(u.shoes), topLayer(u.top)];
  if (u.robe) out.push(robeLayer(u.robe));
  if (u.belt) out.push(beltLayer(u.belt));
  if (u.cape) out.push(capeLayer(u.cape));
  if (u.pauldrons) out.push(pauldronLayer(u.pauldrons));
  for (const h of u.headwear ?? []) out.push(headwearLayer(h));
  if (u.regalia?.length) out.push(regaliaLayer(u.regalia));
  return out;
}
